import * as THREE from 'three';
import type { TableCamera } from '@somnhold/render';

export interface BoardInputHandlers {
  /** `mouse` is true for a real mouse (left selects, right moves); false for touch and pens. */
  tap(x: number, y: number, button: number, mouse: boolean): void;
  hover?(x: number, y: number): void;
  /** Touch only: a finger held still for a moment. When set, the release that follows is not a tap. */
  longPress?(x: number, y: number): void;
  /** The held finger slides (after a long press, or a drag that began on `grab`): the hero follows it. */
  holdMove?(x: number, y: number): void;
  /** Whether a drag starting here grabs the hero (it follows right away instead of the camera panning). */
  grab?(x: number, y: number): boolean;
  /**
   * A one-finger (left button) drag that starts here draws instead of panning when this says so (`first`):
   * a wall traced cell by cell. Every move after it calls again; `paintEnd` when the finger lifts.
   */
  paint?(x: number, y: number, first: boolean): boolean;
  paintEnd?(): void;
}

/** As in battles: hold still this long and the hero starts following the finger. */
const LONG_PRESS_MS = 260;

/**
 * Board gestures shared by the screens: one finger / left drag pans, right drag or two fingers
 * rotate (two fingers sliding together pan too), wheel or pinch zooms, a short press without movement is a tap.
 */
export class BoardInput {
  private pointers = new Map<number, { x: number; y: number; sx: number; sy: number; t: number; button: number; held?: boolean }>();
  private holdTimer = 0;
  private mode: 'none' | 'pan' | 'rotate' | 'pinch' | 'paint' = 'none';
  private pinch = { dist: 0, angle: 0, mx: 0, my: 0 };
  private offs: (() => void)[] = [];
  private ray = new THREE.Raycaster();
  private plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  mouse = { x: 0, y: 0, inside: false };
  /** Where a ray meets the ground when it isn't flat (terraced land); null leaves the y = 0 plane. */
  pick: ((ray: THREE.Ray) => THREE.Vector3 | null) | null = null;

  constructor(
    private el: HTMLElement,
    private cam: TableCamera,
    private h: BoardInputHandlers,
  ) {
    const on = <K extends keyof HTMLElementEventMap>(t: EventTarget, ev: K, fn: (e: HTMLElementEventMap[K]) => void, o?: AddEventListenerOptions): void => {
      t.addEventListener(ev, fn as EventListener, o);
      this.offs.push(() => t.removeEventListener(ev, fn as EventListener, o));
    };
    on(el, 'pointerdown', (e) => {
      el.setPointerCapture?.(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: performance.now(), button: e.button });
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()] as unknown as [{ x: number; y: number }, { x: number; y: number }];
        // A second finger stops a stroke being drawn: two fingers move the board.
        if (this.mode === 'paint') h.paintEnd?.();
        this.mode = 'pinch';
        this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), angle: Math.atan2(b.y - a.y, b.x - a.x), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
      } else this.mode = 'none';
      window.clearTimeout(this.holdTimer);
      if (e.pointerType !== 'mouse' && this.pointers.size === 1 && h.longPress) {
        const id = e.pointerId;
        this.holdTimer = window.setTimeout(() => {
          const p = this.pointers.get(id);
          if (!p || this.mode !== 'none' || this.pointers.size !== 1) return;
          p.held = true;
          h.longPress?.(p.x, p.y);
        }, LONG_PRESS_MS);
      }
    });
    on(window, 'pointermove', (e) => this.move(e));
    on(window, 'pointerup', (e) => this.up(e));
    on(window, 'pointercancel', (e) => this.up(e));
    on(el, 'wheel', (e) => {
      e.preventDefault();
      cam.zoom(Math.exp(e.deltaY * 0.0012));
    }, { passive: false });
    on(el, 'contextmenu', (e) => e.preventDefault());
    on(el, 'pointerleave', () => (this.mouse.inside = false));
  }

  private move(e: PointerEvent): void {
    this.mouse.x = e.clientX;
    this.mouse.y = e.clientY;
    this.mouse.inside = e.target === this.el;
    if (this.mouse.inside) this.h.hover?.(e.clientX, e.clientY);
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()] as unknown as [{ x: number; y: number }, { x: number; y: number }];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const angle = Math.atan2(b.y - a.y, b.x - a.x);
      if (this.pinch.dist > 0) this.cam.zoom(this.pinch.dist / dist);
      let da = angle - this.pinch.angle;
      if (da > Math.PI) da -= Math.PI * 2;
      if (da < -Math.PI) da += Math.PI * 2;
      this.cam.rotate(da);
      // Both fingers sliding together drag the board, so it pans even while one finger alone draws.
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      this.cam.pan(-(mx - this.pinch.mx) * 0.03, (my - this.pinch.my) * 0.039);
      this.pinch = { dist, angle, mx, my };
      return;
    }
    if (p.held) {
      this.h.holdMove?.(e.clientX, e.clientY);
      return;
    }
    if (this.mode === 'none' && Math.hypot(e.clientX - p.sx, e.clientY - p.sy) > 14) {
      window.clearTimeout(this.holdTimer);
      // A finger that starts on the hero drags it along instead of moving the camera.
      if (e.pointerType !== 'mouse' && this.h.grab?.(p.sx, p.sy)) {
        p.held = true;
        this.h.longPress?.(e.clientX, e.clientY);
        return;
      }
      if (p.button === 0 && !e.shiftKey && this.h.paint?.(p.sx, p.sy, true)) this.mode = 'paint';
      else this.mode = p.button === 2 || e.shiftKey ? 'rotate' : 'pan';
    }
    if (this.mode === 'paint') {
      this.h.paint?.(e.clientX, e.clientY, false);
      return;
    }
    if (this.mode === 'rotate') this.cam.rotate(-dx * 0.006, dy * 0.004);
    else if (this.mode === 'pan') this.cam.pan(-dx * 0.03, dy * 0.039);
  }

  private up(e: PointerEvent): void {
    const p = this.pointers.get(e.pointerId);
    this.pointers.delete(e.pointerId);
    if (!p) return;
    window.clearTimeout(this.holdTimer);
    if (this.mode === 'paint') {
      if (this.pointers.size === 0) this.mode = 'none';
      this.h.paintEnd?.();
      return;
    }
    const wasGesture = this.mode !== 'none';
    const pinched = this.mode === 'pinch';
    if (this.pointers.size === 0) this.mode = 'none';
    if (p.held || e.type === 'pointercancel' || pinched) return;
    // A quick click is a click even if the hand slipped a little.
    if (wasGesture && !(performance.now() - p.t < 300 && Math.hypot(e.clientX - p.sx, e.clientY - p.sy) < 30)) return;
    this.h.tap(e.clientX, e.clientY, p.button, e.pointerType === 'mouse');
  }

  /** Ground point under a screen position (on y = 0, or on the land's own height with `pick`). */
  groundAt(x: number, y: number): THREE.Vector3 | null {
    const ndc = new THREE.Vector2((x / window.innerWidth) * 2 - 1, -(y / window.innerHeight) * 2 + 1);
    this.ray.setFromCamera(ndc, this.cam.camera);
    if (this.pick) return this.pick(this.ray.ray);
    const hit = new THREE.Vector3();
    return this.ray.ray.intersectPlane(this.plane, hit) ? hit : null;
  }

  dispose(): void {
    window.clearTimeout(this.holdTimer);
    for (const f of this.offs) f();
  }
}
